import React, { useContext, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Auth } from 'aws-amplify';
import {
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  useTheme,
  Avatar,
} from '@mui/material';
import HitiqSignInLogo from '../../assets/img/HITIQLogoVariant.png';
import { LoginDomainService } from '../login/loginDomainService';
import { di } from '../../state/inversify.config';
import { LegalContent } from '../../components/legalLinks/legal';
import { UserNotificationContext } from '../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../components/userNotification/userNotificationType.enum';
import { AlertDialog } from '../../components/alertDialog/alertDialog';
import { Button } from '../../components/button/button';

export function SignUpWithConsent() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addUserNotification } = useContext(UserNotificationContext);
  const parentRef = useRef(null);

  const [showLegalContentModal, setShowLegalContentModal] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [legalType, setLegalType] = useState('');
  const [legalHeader, setLegalHeader] = useState('');

  const signUpGuardian = () => {
    if (!accepted) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('LEGAL_ERRORS'),
        UserNotificationType.ERROR,
      );
      return;
    }
    setShowConsentForm(true);
  };

  const handleTermsAndPrivacyPolicyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked } = event.target;
    setAccepted(checked);
  };

  const handleLinkClick = (e: any) => {
    e.preventDefault();
    if (e.target.tagName === 'A') {
      if (e.target.getAttribute('href') === '/terms') {
        setShowLegalContentModal(true);
        setLegalType('terms');
        setLegalHeader(t('TERMS_OF_USE_TITLE'));
      } else if (e.target.getAttribute('href') === '/privacy') {
        setShowLegalContentModal(true);
        setLegalType('privacy');
        setLegalHeader(t('PRIVACY_POLICY_HEADER'));
      }
    }
  };

  const onSignUpConfirm = async () => {
    await Auth.federatedSignIn({
      customProvider: 'HitiqLogin',
    });
  };

  return (
    <>
      <Stack spacing={1} direction="column" mt={2}>
        <Button
          color="primary"
          type="submit"
          testId="signUp"
          fullWidth
          variant="contained"
          onClick={signUpGuardian}
          label={t('SIGN_IN_HITIQ')}
          startIcon={
            <Avatar src={HitiqSignInLogo} sx={{ width: 20, height: 20 }} />
          }
          sx={{
            backgroundColor: theme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '1rem',
            minHeight: '50px',
            marginTop: '1rem',
            '&:hover': {
              backgroundColor: theme.palette.text.primary,
            },
          }}
        />

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                title="termsAndPrivacyPolicy"
                onChange={handleTermsAndPrivacyPolicyChange}
                name="termsAndPrivacyPolicy"
                sx={{ color: theme.palette.text.primary }}
              />
            }
            label={
              <Typography
                ref={parentRef}
                onClick={handleLinkClick}
                variant="subtitle2"
                color={theme.palette.text.primary}
                sx={{
                  cursor: 'text',
                }}
              >
                <Trans i18nKey="AGREE_AND_READ_TERMS_AND_CONDITION">
                  <a
                    href="/terms"
                    style={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    placeholder_text
                  </a>
                  <a
                    href="/privacy"
                    style={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    placeholder_text
                  </a>
                </Trans>
              </Typography>
            }
          />
        </FormGroup>
      </Stack>

      {/* legal modal */}
      <AlertDialog
        title={legalHeader}
        setOpen={setShowLegalContentModal}
        open={showLegalContentModal}
        content={<LegalContent legal={legalType} />}
      />

      {/* consent modal */}
      <AlertDialog
        actions
        title={t('CONSENT_FORM_TITLE')}
        setOpen={setShowConsentForm}
        open={showConsentForm}
        onConfirm={onSignUpConfirm}
        confirmLabel={t('CONFIRM')}
        cancelLabel={t('CANCEL')}
        content={
          <Trans i18nKey="CONSENT_FORM_CONTENT">
            <a
              target="_blank"
              href="https://www.hitiq.com/privacy-policy"
              rel="noopener noreferrer"
            >
              placeholder_text
            </a>
          </Trans>
        }
      />
    </>
  );
}
